import { JSX, useEffect, useState } from "react";

import { Login } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardActions, CardContent, Theme, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import ReactGA from "react-ga4";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { setDefaultAuthorizationToken } from "src/api/axios";
import OutageAlerts from "src/components/OutageAlerts/OutageAlerts";
import images from "src/constants";
import { UserApiRole, UserAppRole } from "src/constants/userRoles.enum";
import { getUserPinnedLocations } from "src/lib/settings.helper";
import { checkUserOrganizationAccess, getApiRolesFromUser, getAppRolesFromUser, signOutSilent } from "src/lib/user.helper";
import useAppPinnedStore from "src/store/pinned.store";

const userPossibleAppRoles = new Set([
  UserAppRole.DocumentViewer.toString(),
  UserAppRole.InsightViewer.toString(),
  UserAppRole.MallViewer.toString(),
  UserAppRole.ShopViewer.toString(),
  UserAppRole.StationViewer.toString(),
]);

const userPossibleApiRoles = new Set([
  UserApiRole.Superadmin.toString(),
  UserApiRole.Admin.toString(),
  UserApiRole.Superuser.toString(),
  UserApiRole.User.toString(),
]);

function KeycloakSignIn(): JSX.Element | null {
  const theme = useTheme();
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { appPinned, setAppPinned } = useAppPinnedStore();

  const handleUnauthorizedUser = (message: string): void => {
    setErrorMessage(t(message));
    signOutSilent(queryClient, auth);
  };

  useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading) {
      navigate("/", { replace: true });
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate]);

  const handleLogin = async (): Promise<void> => {
    const authenticatedUser = await auth.signinPopup();
    if (authenticatedUser) {
      const userAppRoles = getAppRolesFromUser(authenticatedUser);
      const userApiRoles = getApiRolesFromUser(authenticatedUser);

      const hasValidAppRole = userAppRoles.some((role: string) => userPossibleAppRoles.has(role));
      const hasValidApiRole = userApiRoles.some((role: string) => userPossibleApiRoles.has(role));

      if (!hasValidAppRole || !hasValidApiRole) {
        const userName = authenticatedUser.profile?.preferred_username;
        handleUnauthorizedUser(
          t(`Your account is awaiting approval by ASE. Please contact the ASE support team for further assistance.`, { username: userName })
        );
      } else if (checkUserOrganizationAccess(authenticatedUser)) {
        setDefaultAuthorizationToken(authenticatedUser.access_token);

        ReactGA.event("login_success", {
          username: authenticatedUser.profile?.preferred_username,
        });

        const sid = authenticatedUser.profile?.sid;
        if (sid) {
          const encodedUserId = btoa(sid);
          ReactGA.set({ userId: encodedUserId });
        }

        await getUserPinnedLocations(appPinned, setAppPinned);
      } else {
        handleUnauthorizedUser("User Unauthorized: Not a member of an allowed Organisation");
      }
    }
  };

  const handleLoginClick = (): void => {
    void handleLogin();
  };

  return (
    <>
      <OutageAlerts />
      <Box sx={getStyles(theme).outerBox}>
        <Card sx={getStyles(theme).innerBox} variant='outlined'>
          <CardContent>
            <Box sx={getStyles(theme).headerRow}>
              <Box>
                <img src={images.logo} width={48} height={48} alt='DiamondApp' />
              </Box>
              <Typography variant='h6' sx={getStyles(theme).heading}>
                Welcome to Diamond App
              </Typography>
            </Box>
            {!!errorMessage && (
              <Alert severity='error' sx={getStyles(theme).alert}>
                {errorMessage}
              </Alert>
            )}
          </CardContent>
          <CardActions>
            <Button
              color='primary'
              variant='contained'
              fullWidth
              onClick={handleLoginClick}
              disabled={auth.isLoading}
              endIcon={<Login sx={getStyles(theme).icon} />}
            >
              {auth.isLoading ? "Sign In Popup already open" : "Sign In"}
            </Button>
          </CardActions>
        </Card>
      </Box>
    </>
  );
}

// Some Styles
const getStyles = (theme: Theme) => ({
  heading: { fontSize: theme.fontSizes.display },
  alert: { mt: 2 },
  outerBox: { width: "100%", height: "calc(100vh - 108px)", display: "flex", justifyContent: "center", alignItems: "center" },
  innerBox: { width: 400, maxWidth: 400, p: 1 },
  headerRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: 1,
  },
  icon: { color: theme.palette.surface.card },
});

export default KeycloakSignIn;
